"use client";
import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../../hooks/store-hooks";
import { History, Search } from "../../../icons";
import PopoverProfile from "../popover";

import { useLocation, useNavigate } from "react-router-dom";
import { ROUTES } from "../../../constants/routes";
import {
  useAddSearchHistoryMutation,
  useGetSearchHistoryDataQuery,
} from "../../../store/api/searchApi";
import Notification from "../../notification";
import { FynoProps } from "../../types";
import { getLocalStorage } from "../../../helpers/storage";

let searchInputRef: React.RefObject<HTMLInputElement> | null = null;

export const focusSearchField = () => {
  if (searchInputRef && searchInputRef.current) {
    searchInputRef.current.focus();
  }
};

const Header = () => {
  const { selectedType } = useAppSelector((state: any) => state.search || {});
  const user = JSON.parse(getLocalStorage("userData") || "{}");
  const [fynoProp, setFynoProp] = useState<FynoProps>({
    distinctId: "",
    workspaceId: "",
    integrationId: "",
    signature: "",
  });
  useEffect(() => {
    if (user?._id && user?.notify) {
      setFynoProp((prev) => ({
        ...prev,
        distinctId: user._id,
        workspaceId: user.notify.workspace_id,
        integrationId: user.notify.integration_id,
        signature: user.notify.token,
      }));
    }
  }, [
    user?._id,
    user?.notify?.workspace_id,
    user?.notify?.integration_id,
    user?.notify?.token,
  ]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setActiveTab] = useState<string>("home");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchHistoryOpen, setIsSearchHistoryOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  const [triggerAddSearchHistory] = useAddSearchHistoryMutation();
  const { data: searchHistoryData, isLoading } = useGetSearchHistoryDataQuery({
    skip: !isSearchHistoryOpen, // Only fetch when recent search history modal is open
  });

  const location = useLocation();
  const navigate = useNavigate();

  const ROUTING_MAPPING = [
    {
      title: "Home",
      isFocused: location.pathname.includes(ROUTES.HOME),
      key: "home",
      redirectionUrl: ROUTES.HOME,
    },
    {
      title: "Academy",
      isFocused: location.pathname.includes(ROUTES.ACADEMY_DEFAULT),
      key: "academy",
      redirectionUrl: ROUTES.ACADEMY_DEFAULT,
    },
    {
      title: "Money",
      isFocused: location.pathname.includes(ROUTES.MONEY_DEFAULT),
      key: "money",
      redirectionUrl: ROUTES.MONEY_DEFAULT,
    },
    {
      title: "Events",
      isFocused: location.pathname.includes(ROUTES.EVENTS),
      key: "events",
      redirectionUrl: ROUTES.EVENTS,
    },
  ];

  const handleNavigation = (tab: any) => {
    setActiveTab(tab.key);

    navigate(tab.redirectionUrl);
  };

  const handleSearch = async () => {
    if (searchQuery.trim()) {
      const payload = {
        query: searchQuery,
        type: selectedType,
      };
      await triggerAddSearchHistory(payload); // Save the search query to history
      setSearchQuery("");
      navigate(`/search?q=${searchQuery}`);
      setIsSearchHistoryOpen(false);
    }
  };

  const handleSearchHistoryClick = (query: string) => {
    setSearchQuery("");
    setIsSearchHistoryOpen(false);
    navigate(`/search?q=${query}`);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!searchRef?.current?.contains(event.target as Node)) {
        setIsSearchHistoryOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  searchInputRef = useRef<HTMLInputElement>(null);

  return (
    <header className="sticky top-0 bg-white z-20">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 mx-5">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <div
              className="flex-shrink-0 mr-10 hover:cursor-pointer"
              onClick={() => handleNavigation(ROUTING_MAPPING[0])}
            >
              <img
                alt=""
                src="/assets/logo/one-square.svg"
                width={47}
                height={47}
              />
            </div>
            {/* Search Bar */}
            <div className="relative" ref={searchRef}>
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                onFocus={() => setIsSearchHistoryOpen(true)}
                className="bg-gray-100 h-10 px-8 pr-10 rounded-xl-10 text-black outline-0"
              />
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                <Search width={14} height={14} fillColor="#808080" />
              </div>

              {isSearchHistoryOpen &&
                !isLoading &&
                searchHistoryData?.data?.history && (
                  <div className="absolute top-full left-0 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 rounded-[10px] overflow-y-auto max-h-[170px]">
                    <div className="py-2">
                      <h3 className="px-4 py-2 text-sm font-semibold text-gray-500">
                        Recent Searches
                      </h3>
                      {searchHistoryData?.data?.history?.map(
                        (item: any, index: number) => (
                          <div
                            key={index}
                            className="flex flex-row items-center px-4 py-2 hover:bg-gray-100 cursor-pointer gap-x-4"
                            onClick={() => handleSearchHistoryClick(item.query)}
                          >
                            <History />
                            {item.query}
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}
            </div>
          </div>
          {/* Navigation Tabs */}
          <div className="flex flex-row space-x-3 absolute left-1/2 transform -translate-x-1/2">
            {ROUTING_MAPPING.map((tab, index) => (
              <div
                key={index}
                className={`px-3 py-2 cursor-pointer relative font-semibold text-base ${
                  tab.isFocused
                    ? "text-white bg-black rounded-lg"
                    : "text-gray-500"
                }`}
                onClick={() => handleNavigation(tab)}
              >
                {tab.title}
                {tab.isFocused && (
                  <span className="absolute inset-y-0 left-0 w-px bg-white animate-highlight"></span>
                )}
                {tab.isFocused ? (
                  <span className="absolute inset-y-0 right-0 w-px bg-white animate-highlight"></span>
                ) : null}
              </div>
            ))}
          </div>
          {/* Icons */}
          <div className="flex items-center space-x-3">
            {fynoProp?.distinctId && (
              <div className="relative">
                <Notification fynoProp={fynoProp} />
              </div>
            )}

            <PopoverProfile />
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
